<template>
  <div>
    <div class="container single spHidden">
      <breadcrumbs :options="{ organizer }" page="organizerReviewDetail" />
    </div>
    <div class="container">
      <div class="container-main">
        <h1 class="organizerName">
          {{ organizer.name }} <br v-if="$store.isSP" />
          の口コミ・評判
        </h1>
        <div v-if="showAllReviewCount" class="organizerRating">
          <div class="organizerRating-label">みんなの口コミ</div>
          <div class="organizerRating-star">
            <rating-star :number-star="avgRating" />
          </div>
          <div class="organizerRating-score">
            <span class="ratingNumber">
              {{ avgRating }}
            </span>
            ({{ allReviewCount }}件)
          </div>
        </div>
        <div class="organizerRatingOverview">
          <ul class="flex">
            <li>
              参加人数・<br />男女比 <br />
              <div>{{ toFixedMaxDecimalRating(numberParticipantSatisfaction) }}</div>
            </li>
            <li>
              スタッフの<br />対応 <br />
              <div>{{ toFixedMaxDecimalRating(staffSupportSatisfaction) }}</div>
            </li>
            <li>
              会場の<br />雰囲気<br />
              <div>{{ toFixedMaxDecimalRating(venueSatisfaction) }}</div>
            </li>
            <li>
              料理や<br />飲み物<br />
              <div>{{ toFixedMaxDecimalRating(foodDrinkSatisfaction) }}</div>
            </li>
            <li>
              企画<br />内容<br />
              <div>{{ toFixedMaxDecimalRating(eventContentSatisfaction) }}</div>
            </li>
          </ul>
        </div>
        <div class="organizerDescription">
          <div class="organizerDescription-text">
            {{ organizerDescription }}
          </div>
        </div>
        <div id="organizerReviews" class="organizerReviews">
          <h2 class="organizerReviews-header">みんなの口コミ</h2>
          <div v-if="avgRating" class="organizerRatingDetails">
            <div
              v-for="(rating, index) in RATING_ARRAY"
              :key="index"
              class="organizerRatingDetails-item"
              @click="loadAndScrollToReviewsByRating(rating)"
            >
              <div class="label">星{{ rating }}つ</div>
              <div
                v-if="ratingPercentageByRating(rating) === 100"
                :class="`percentage percentage-${ratingPercentageByRating(rating)}`"
                class="customBorder"
              ></div>
              <div v-else :class="`percentage percentage-${ratingPercentageByRating(rating)}`"></div>
              <div class="percentNumber">{{ ratingPercentageByRating(rating) }}%</div>
            </div>
          </div>
          <div v-if="hasReview">
            <div class="organizerReviews-header">
              <div class="ratingInfo">
                <div v-if="isFilterByRating" class="filterByRating">
                  <span class="textBold">星{{ byRating }}つ</span>
                  <span class="clearFilter" @click="loadAndClearFilter">フィルタをクリア</span>
                </div>
                <span>評価：{{ reviewCount }}件｜口コミ：{{ viewableReviewCount }}件</span>
              </div>
            </div>
            <div class="organizerReviews-content">
              <organizer-rating-review-item v-for="(userReview, index) in userReviews" :key="index" :user-review="userReview" />
            </div>
            <button v-if="!isLastPage" class="commonButton" @click="loadMoreReviews">もっと見る</button>
          </div>
          <div v-else class="organizerReviews noReviews">まだレビューはありません</div>
        </div>
        <button class="commonButton btn-primary-50" @click="redirectToPromoterEventsPage">この主催者のイベントをもっと見る</button>

        <div v-if="organizerEvents && organizerEvents.length > 0" class="organizerEvents">
          <div class="organizerEvents-header">{{ organizer.name }}のイベント</div>
          <recommend-event-list-sp
            v-if="$store.isSP"
            :events="organizerEvents"
            class="marginB5"
            @plus-bookmark-count="applyChangeBookmarkCount(1)"
            @minus-bookmark-count="applyChangeBookmarkCount(-1)"
          />
          <scrollable-box v-if="!$store.isSP" :enable-scroll="organizerEventsForPc.length > 3">
            <ul class="recommendPC scrollable">
              <li v-for="(event, index) in organizerEventsForPc" :key="index" class="recommendPC-item">
                <event-card-pc
                  :event="event"
                  :display-ticket-top="true"
                  @plus-bookmark-count="applyChangeBookmarkCount(1)"
                  @minus-bookmark-count="applyChangeBookmarkCount(-1)"
                />
              </li>
            </ul>
          </scrollable-box>
        </div>

        <promoter-area-links
          v-if="organizerPrefectures && organizerPrefectures.length > 0"
          :organizer="organizer"
          :organizer-prefectures="organizerPrefectures"
        />

        <div v-if="organizer.company_name || organizer.zipcode || organizer.address" class="organizerInfo">
          <div class="organizerInfo-label">基本情報</div>
          <div class="organizerInfo-content">
            <div v-if="organizer.company_name" class="organizerInfo-content-item">
              <div class="textLabel">運営会社</div>
              <div class="textContent">{{ organizer.company_name }}</div>
            </div>
            <div v-if="organizer.zipcode" class="organizerInfo-content-item">
              <div class="textLabel">郵便番号</div>
              <div class="textContent">{{ organizer.zipcode }}</div>
            </div>
            <div v-if="organizer.address" class="organizerInfo-content-item">
              <div class="textLabel">住所</div>
              <div class="textContent">{{ organizer.address }}</div>
            </div>
          </div>
        </div>
        <div class="organizerRatingTerms">
          <div class="organizerRatingTerms-label" @click="isShowedTerms = !isShowedTerms">
            評価点と口コミについて
            <icon v-if="isShowedTerms" fas="fa-chevron-up" class="showButton"></icon>
            <icon v-else fas="fa-chevron-down" class="showButton"></icon>
          </div>
          <ul v-if="isShowedTerms" class="organizerRatingTerms-content">
            <li v-for="(term, index) in ORGANIZER_RATING_TERMS" :key="index" class="organizerRatingTerms-content-item">
              {{ term }}
            </li>
          </ul>
        </div>
        <div
          v-if="eventDefaultSetting && (eventDefaultSetting.caption_for_organizer_message || eventDefaultSetting.image_for_organizer_message_url)"
          class="organizerDefaultSetting"
        >
          <h2 class="organizerDefaultSetting-header">主催者から一言</h2>
          <div :class="{ marginB5: $store.isSP }" class="organizerDefaultSetting-content contentBlock commonCard">
            <article class="detailArticle marginT4">
              <figure>
                <common-lazy-load-image
                  v-if="eventDefaultSetting.image_for_organizer_message_url"
                  :image-url="eventDefaultSetting.image_for_organizer_message_url"
                  :alt="eventDefaultSetting.alt_for_organizer_message"
                />
                <figcaption
                  v-if="eventDefaultSetting.caption_for_organizer_message"
                  class="textJustify grayColor"
                  v-html="$sanitizedXssHtml(eventDefaultSetting.caption_for_organizer_message)"
                />
              </figure>
            </article>
          </div>
        </div>
        <custom-nuxt-link to="/review" class="organizer-list-link"> 主催者一覧に戻る </custom-nuxt-link>
      </div>
      <div v-if="!$store.isSP" class="container-side">
        <side-content-pc />
      </div>
    </div>
    <div class="container single breadcrumbs-footer">
      <breadcrumbs :options="{ organizer }" page="organizerReviewDetail" display-mode="footer" />
    </div>
  </div>
</template>

<script>
const RecommendEventListSp = defineAsyncComponent(() => import('@/components/event/RecommendEventListSp.vue'))
const EventCardPc = defineAsyncComponent(() => import('@/components/event/EventCardPc.vue'))
const RatingStar = defineAsyncComponent(() => import('@/components/RatingStar.vue'))
const ScrollableBox = defineAsyncComponent(() => import('@/components/ScrollableBox.vue'))
import { buildQueryString } from '@/utils/url-action'
const CommonLazyLoadImage = defineAsyncComponent(() => import('@/components/CommonLazyLoadImage.vue'))
const Breadcrumbs = defineAsyncComponent(() => import('@/components/Breadcrumbs.vue'))
const PromoterAreaLinks = defineAsyncComponent(() => import('@/components/review/PromoterAreaLinks.vue'))
import { useMainStore } from '@/store'
import { callWithNuxt } from 'nuxt/app'
import { ref } from 'vue'
const SideContentPc = defineAsyncComponent(() => import('@/components/side/SideContentPc.vue'))
const CustomNuxtLink = defineAsyncComponent(() => import('@/components/CustomNuxtLink.vue'))
const OrganizerRatingReviewItem = defineAsyncComponent(() => import('@/components/review/OrganizerRatingReviewItem.vue'))

const RATING_ARRAY = [5, 4, 3, 2, 1]
const ORGANIZER_RATING_TERMS = [
  '評価点は、イベント参加後の参加者アンケートを元に算出されます。',
  '評価の偏りを防ぐため、口コミが一定数集まるまで、評価点や口コミは表示されません。',
  '事実確認が取れないもの、攻撃的な表現が含まれるものなど、運営側で不適切と判断した口コミは表示されません。',
  '口コミは全て、machicon JAPANをご利用のお客様により書かれたものです。各主催者及びmachicon JAPANは、その内容の当否については保証できかねます。お客様ご自身の最終判断でご利用くださいますよう、お願いいたします。'
]
const NUM_OF_EVENT_SHOW_FOR_PC = 10
const MIN_ALL_REVIEW_COUNT_TO_DISPLAY = 9
const MIN_TO_FIXED_DECIMAL_PLACES = 2
const MAX_RATING_COUNT_PER_PAGE = 5

export default {
  components: {
    RecommendEventListSp,
    EventCardPc,
    SideContentPc,
    RatingStar,
    CommonLazyLoadImage,
    Breadcrumbs,
    CustomNuxtLink,
    OrganizerRatingReviewItem,
    PromoterAreaLinks,
    ScrollableBox
  },
  async setup() {
    const app = useNuxtApp()
    const store = useMainStore()
    const route = useRoute()
    const params = route.params
    const requests = []
    const organizerSlug = params.organizer
    const byRating = route.query && route.query.by_rating
    const query = byRating ? { by_rating: byRating } : {}

    const isFilterByRating = ref(null)
    const reviewCount = ref(null)
    const userReviews = ref(null)
    const viewableReviewCount = ref(null)
    const currentPage = ref(1)
    const isLastPage = ref(null)

    const organizerReviewRequest = store.dispatch('apiGet', `review/${organizerSlug}/${buildQueryString(query)}`).then(response => {
      isLastPage.value = response.data.is_last_page
      const commonResponse = {
        organizer: response.data.organizer,
        avgRating: response.data.avg_rating,
        numberParticipantSatisfaction: response.data.number_of_participant_satisfaction,
        staffSupportSatisfaction: response.data.staff_support_satisfaction,
        venueSatisfaction: response.data.venue_satisfaction,
        foodDrinkSatisfaction: response.data.food_drink_satisfaction,
        eventContentSatisfaction: response.data.event_content_satisfaction,
        allReviewCount: response.data.review_count,
        reviewDetails: response.data.reviews_by_rating,
        organizerEvents: response.data.organizer_events,
        allReviews: response.data.all_reviews,
        eventDefaultSetting: response.data.event_default_setting,
        organizerPrefectures: response.data.organizer_prefectures || [],
        currentPage,
        isLastPage
      }
      if (byRating) {
        const reviewInfosByRating = response.data.reviews_by_rating.find(item => item.rating === Number(byRating))
        callWithNuxt(app, () =>
          useHeadParams({
            $route: route,
            organizer: response.data.organizer
          })
        )
        isFilterByRating.value = true
        reviewCount.value = reviewInfosByRating.total_count_by_rating
        userReviews.value = reviewInfosByRating.viewable_reviews_by_rating
        viewableReviewCount.value = reviewInfosByRating.viewable_reviews_count_by_rating
        return {
          ...commonResponse,
          isFilterByRating,
          reviewCount,
          userReviews,
          viewableReviewCount
        }
      }
      callWithNuxt(app, () =>
        useHeadParams({
          $route: route,
          organizer: response.data.organizer
        })
      )
      isFilterByRating.value = false
      ;(reviewCount.value = response.data.review_count), (userReviews.value = response.data.all_reviews.all_user_reviews)
      viewableReviewCount.value = response.data.all_reviews.viewable_reviews_count
      return {
        ...commonResponse,
        isFilterByRating,
        reviewCount,
        userReviews,
        viewableReviewCount
      }
    })

    requests.push(organizerReviewRequest)
    try {
      const [organizerReviews] = await Promise.all(requests)
      return { ...organizerReviews }
    } catch (e) {
      const errorCode = parseInt(e.response && e.response.status)
      await app.$error(errorCode)
    }
  },
  data() {
    return {
      isShowedTerms: false,
      RATING_ARRAY,
      ORGANIZER_RATING_TERMS,
      bookmarkedUserCount: 0,
      byRating: null
    }
  },
  computed: {
    organizerDescription() {
      const defaultDescription = `【${this.organizer.name}主催イベントを掲載中】街コンや婚活パーティー・お見合いパーティーなど、様々な出会いのイベントを開催中。バリエーションも豊富なのでご希望のイベントがきっと見つかります。${this.organizer.name}主催イベントをお探しの方は【${this.organizer.name}】ページからお探しください。`
      return this.organizer.meta_description || defaultDescription
    },
    organizerEventsForPc() {
      return this.organizerEvents.slice(0, NUM_OF_EVENT_SHOW_FOR_PC)
    },
    showAllReviewCount() {
      return this.allReviewCount > MIN_ALL_REVIEW_COUNT_TO_DISPLAY
    },
    hasReview() {
      return this.allReviewCount > 0
    }
  },
  methods: {
    ratingPercentageByRating(rating) {
      const element = this.reviewDetails.find(item => item.rating === Number(rating))
      return element ? element.percentage : 0
    },
    applyChangeBookmarkCount(num) {
      this.bookmarkedUserCount += num
    },
    redirectToPromoterEventsPage() {
      const path = `/promoters/${this.$route.params.organizer}`
      this.$router.push({ path: path })
    },
    loadMoreReviews() {
      this.fetchMoreReviews(this.byRating, false)
    },
    loadAndScrollToReviewsByRating(rating) {
      this.fetchMoreReviews(rating, true)
      this.scrollToUserReviews()
    },
    loadAndClearFilter() {
      this.isFilterByRating = false
      this.byRating = null
      this.fetchMoreReviews(this.byRating, true)
      this.scrollToUserReviews()
    },
    fetchMoreReviews(rating, replace) {
      if (rating) {
        this.byRating = rating
        this.isFilterByRating = true
      }
      const currentShowedReviewCount = replace ? 0 : this.userReviews.length
      const organizerSlug = this.$route.params.organizer
      const nextPage = replace ? 1 : this.currentPage + 1
      const query = {
        by_rating: this.byRating,
        showed_count: currentShowedReviewCount,
        organizer_slug: organizerSlug,
        page: nextPage,
        per: MAX_RATING_COUNT_PER_PAGE
      }
      this.$store.dispatch('apiGet', `fetch_more_user_reviews/${buildQueryString(query)}`).then(response => {
        if (replace) {
          this.userReviews = response.data.more_user_reviews
        } else {
          this.userReviews = this.userReviews.concat(response.data.more_user_reviews)
        }
        this.currentPage = nextPage
        this.isLastPage = response.data.is_last_page
      })
    },
    scrollToUserReviews() {
      this.$nextTick(() => {
        const el = document.getElementById('organizerReviews')
        window.scrollTo(0, el.offsetTop - 40)
      })
    },
    toFixedMaxDecimalRating(value) {
      return !value ? ' - ' : value.toFixed(MIN_TO_FIXED_DECIMAL_PLACES)
    }
  }
}
</script>

<style lang="scss" scoped>
.organizerName {
  text-align: center;
  margin: 30px 60px;
  font-size: 20px;
  @media screen and (max-width: 320px) {
    font-size: 18px;
  }
  font-weight: bold;
  color: $black-2a;
}

.organizerRating {
  margin: 30px auto;
  display: flex;
  width: fit-content;
  align-items: center;
  padding: 0 6px;
  &-label {
    font-size: 14px;
    @media screen and (max-width: 320px) {
      font-size: 12px;
    }
    font-weight: bold;
    margin-right: 5px;
    color: $black-2a;
  }
  &-score {
    display: flex;
    font-size: 14px;
    @media screen and (max-width: 320px) {
      font-size: 12px;
    }
    color: $black-2a;
    align-items: center;
    .ratingNumber {
      margin-right: 4px;
      font-size: 16px;
      @media screen and (max-width: 320px) {
        font-size: 14px;
      }
      font-weight: bold;
    }
  }
}

.organizerRatingOverview {
  max-width: 400px;
  margin: 20px auto;
  ul {
    display: flex;
    li {
      font-size: 12px;
      color: $black;
      padding: 5px;
      border-right: 1px solid $black-2633;
      width: 20%;
      text-align: center;
    }
    li:last-child {
      border-right: none;
    }
    div {
      margin-top: 12px;
      font-weight: bold;
      font-size: 24px;
      color: $black;
    }
  }
}

.organizerDescription {
  color: $black-2a;
  font-size: 14px;
  text-align: center;
  img {
    max-width: 350px;
    max-height: 175px;
    margin: 5px auto;
  }
  @media screen and (max-width: 320px) {
    font-size: 12px;
  }
  letter-spacing: 0;
  &-text {
    text-align: left;
    margin: 0 15px;
  }
}

.organizerInfo {
  margin: 40px 10px 0;
  &-label {
    text-align: left;
    font-weight: bold;
    font-size: 20px;
    @media screen and (max-width: 320px) {
      font-size: 16px;
    }
  }
  &-content {
    margin: 10px 0;
    padding: 15px 0;
    border: 1px solid $gray-d8;
    border-radius: 1.2rem;
    font-size: 14px;
    @media screen and (max-width: 320px) {
      font-size: 12px;
    }
    &-item {
      display: flex;
      padding: 10px;
      .textLabel {
        color: $black-2a;
        width: 25%;
        max-width: 90px;
      }
      .textContent {
        width: 75%;
        margin-left: 10px;
      }
    }
  }
}

.organizerRatingDetails {
  margin: 0 20px;
  font-size: 13px;
  display: grid;
  &-item {
    display: flex;
    margin: 4px 0;
    height: 30px;
    color: $cerulean;
    text-decoration: none;
    cursor: pointer;
    .label {
      margin: auto;
      width: 15%;
      text-align: left;
      @media screen and (max-width: 320px) {
        font-size: 12px;
      }
    }
    .percentage {
      width: 73%;
      background-color: $white;
      border: 1px solid $white-de;
      border-radius: 5px;
      &::after {
        content: '';
        display: block;
        background-color: $gamboge;
        margin-bottom: 10px;
        height: 100%;
        position: relative;
        top: 50%;
        transform: translateY(-50%);
        transition: background-color 0.3s ease;
        cursor: pointer;
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
      }
    }
    .customBorder {
      &::after {
        border-top-right-radius: 5px;
        border-bottom-right-radius: 5px;
      }
    }
    .percentNumber {
      margin: auto;
      width: 12%;
      margin-left: 5px;
      text-align: right;
      @media screen and (max-width: 320px) {
        font-size: 12px;
      }
    }
  }
}

@for $i from 0 through 100 {
  .percentage-#{$i} {
    &::after {
      $value: ($i * 1%);
      width: $value;
    }
  }
}

.organizerRatingTerms {
  margin: 30px 10px;
  border: 1px solid $gray-d8;
  border-radius: 1.2rem;
  padding: 23px 10px;
  color: $black-2a;
  font-size: 14px;
  @media screen and (max-width: 320px) {
    font-size: 13px;
  }
  &-label {
    text-align: center;
    font-weight: bold;
    .showButton {
      margin-left: 10px;
    }
  }
  &-content {
    margin-top: 10px;
    &-item {
      display: flex;
      font-size: 13px;
      @media screen and (max-width: 320px) {
        font-size: 12px;
      }
    }
    li::before {
      content: '・';
    }
  }
}

.organizerDefaultSetting {
  margin: 30px 10px;
  &-header {
    font-weight: bold;
    font-size: 20px;
  }
  &-content {
    .detailArticle {
      figure {
        img {
          width: 100%;
          height: auto;
          display: block;
        }
      }
      figcaption {
        font-size: 1.2rem;
        padding-top: 0.6rem;
      }
    }
  }
  :deep(.contentBlock) {
    margin-top: 10px;
    padding: 20px;
    @include sp {
      padding: 20px 16px;
    }
    .contentBlock-info {
      padding: 1.6rem 0;
      color: $gray-55;
      font-size: 1.2rem;
    }
    .noticeForCovid19 {
      &-item {
        display: flex;
        text-align: center;
        &-image {
          width: 45%;
          max-width: 135px;
          margin-right: 5%;
          img {
            display: inline-block;
            justify-content: center;
            vertical-align: middle;
            width: 48%;
            height: 100%;
            margin: 0 auto;
            &:not(:last-child) {
              margin-right: 2%;
            }
          }
        }
        &-content {
          width: 59%;
          float: left;
          margin: auto auto auto 0;
          color: $gray-6a;
          line-height: 1.8;
          @include pc {
            width: auto;
          }
          &-note {
            font-size: 1rem;
          }
        }
        &:not(:last-child) {
          border-bottom: 1px solid $gray-c6;
          padding-bottom: 17px;
        }
        &:not(:first-child) {
          padding-top: 17px;
        }
      }
    }
  }
}

.organizerReviews {
  margin-top: 20px;
  padding: 20px 0;
  color: $black-2a;
  background-color: $old_lace;
  &-header {
    text-align: center;
    font-weight: bold;
    font-size: 18px;
    @media screen and (max-width: 320px) {
      font-size: 16px;
    }
    .ratingInfo {
      margin: 20px 12px;
      font-size: 15px;
      @media screen and (max-width: 320px) {
        font-size: 13px;
      }
      text-align: left;
      font-weight: normal;
      .filterByRating {
        display: flex;
        .clearFilter {
          margin-left: 10px;
          color: $cerulean;
          text-decoration: none;
          cursor: pointer;
        }
      }
    }
  }
  &-content {
    margin: 20px 10px;
  }
  &.noReviews {
    margin-top: 40px;
    padding: 25px;
    text-align: center;
    font-weight: bold;
    font-size: 18px;
    @media screen and (max-width: 320px) {
      font-size: 16px;
    }
  }
  .commonButton {
    padding: 10px 20px;
    width: fit-content;
    height: fit-content;
    color: $prussian_blue;
    background-color: $white;
    border: 1px solid $gray-d8;
  }
}

.organizerEvents {
  &-header {
    text-align: left;
    font-weight: bold;
    font-size: 20px;
    @media screen and (max-width: 320px) {
      font-size: 18px;
    }
    padding: 20px 10px;
  }
  .recommendPC {
    @include flex;
    justify-content: left;

    &.scrollable {
      display: flex;
      flex-wrap: nowrap;
      gap: 1rem;

      .recommendPC-item {
        flex-shrink: 0;
        width: 22rem; // スクロール時はより小さく
        @media screen and (max-width: 320px) {
          width: 20rem;
        }
      }
    }

    &-item {
      flex-direction: column;
      overflow: auto;
      width: 29rem;
      height: 24.5rem;
      border-radius: 0.3rem;
      background-color: $white;
      @media screen and (max-width: 320px) {
        width: 27rem;
      }
    }
  }
}
.commonButton {
  margin: 25px auto;
  width: 100%;
  max-width: 336px;
}
.breadcrumbs-footer {
  box-shadow: 0 0 3px $gray-c6;
  border-radius: 3px;
}
.organizer-list-link {
  font-size: 16px;
  color: $black-1a;
}
</style>
